import React, { createContext, useContext } from 'react';
import { Platform, type TextStyle } from 'react-native';
import { appearanceTextStyle } from '../components/AppearanceText';
import { AppearancePreviewContext, AppearanceTypographyContext } from '../theme/AppearanceContext';
import { appearanceFontFamily } from '../theme/appearance';

export type ElementStyle = Omit<Partial<TextStyle>, 'fontFamily'> & { fontFamily?: 'system' | 'serif' | 'monospace'; widthPercent?: number; iconSize?: number; opacityPercent?: number };
export const ElementStyleContext = createContext<Record<string, ElementStyle> | null>(null);
const ElementPathContext = createContext('');
const ElementScopeContext = createContext('');
const InheritedTextContext = createContext<ElementStyle>({});
const textKeys = ['fontFamily', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing', 'color', 'textAlign'] as const;
export function elementStyle(value: ElementStyle = {}) {
  const { fontFamily, widthPercent, iconSize, opacityPercent, ...style } = value;
  return { ...style, ...(opacityPercent !== undefined ? { opacity: opacityPercent / 100 } : {}), ...(widthPercent !== undefined && style.width === undefined ? { width: `${widthPercent}%` as const } : {}), ...(fontFamily ? { fontFamily: appearanceFontFamily(fontFamily) ?? (Platform.OS === 'ios' ? 'System' : Platform.OS === 'web' ? 'system-ui' : 'sans-serif') } : {}) };
}
export function elementId(parent: string, site: string) {
  let hash = 2166136261;
  for (const char of `${parent}/${site}`) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return `${(hash >>> 0).toString(16).padStart(8, '0')}_${site}`;
}
export function ElementStyleScope({ value, children, scope = '' }: { value: Record<string, ElementStyle> | null; scope?: string; children: React.ReactNode }) {
  const element = React.isValidElement(children) ? children as React.ReactElement<{ kind: string; child: React.ReactNode }> : null;
  const content = element?.type === EditableElement && element.props.kind === 'scope' ? element.props.child : children;
  return <ElementStyleContext.Provider value={value}><ElementPathContext.Provider value={scope}><ElementScopeContext.Provider value={scope}>{content}</ElementScopeContext.Provider></ElementPathContext.Provider></ElementStyleContext.Provider>;
}
/** Source identities contain no record keys or personal data. Repeated rows
 * share their template's appearance. Original React keys, refs and handlers stay intact. */
export function studioElement(site: string, kind: string, label: string, child: React.ReactElement<any>) {
  return React.createElement(EditableElement, { ...(child.key === null ? {} : { key: child.key }), site, kind, label, child });
}
function EditableElement({ site, kind, label, child }: { site: string; kind: string; label: string; child: React.ReactElement<any> }) {
  const values = useContext(ElementStyleContext);
  const parent = useContext(ElementPathContext);
  const scope = useContext(ElementScopeContext);
  const inherited = useContext(InheritedTextContext);
  const typography = useContext(AppearanceTypographyContext);
  const preview = useContext(AppearancePreviewContext);
  if (!values) return child;
  const id = elementId(parent, site);
  const own = values[id] || {};
  const text = { ...inherited, ...Object.fromEntries(textKeys.filter(key => own[key] !== undefined).map(key => [key, own[key]])) };
  const publicKind = kind.replace('appearance-', '').replace('dom-', '');
  const metadata = { studioElement: id, studioLabel: label, studioKind: publicKind, studioScope: scope };
  const marker = Platform.OS !== 'web' ? {} : kind.startsWith('dom')
    ? { 'data-studio-element': id, 'data-studio-label': label, 'data-studio-kind': publicKind, 'data-studio-scope': scope }
    : { dataSet: { ...child.props.dataSet, ...metadata } };
  const style = elementStyle(own);
  const props: Record<string, any> = { ...marker };
  if (['text', 'input'].includes(publicKind)) {
    const override = [elementStyle(text), style];
    if (kind.startsWith('appearance-')) props.studioStyle = override;
    else props.style = [appearanceTextStyle(child.props.style, preview?.typography || typography), ...override];
  } else {
    const viewStyle = Object.fromEntries(Object.entries(style).filter(([key]) => !textKeys.includes(key as any)));
    if (Object.keys(viewStyle).length) props.style = [child.props.style, viewStyle];
    if (kind === 'icon' || kind === 'indicator') {
      if (own.iconSize !== undefined) {
        props.size = own.iconSize;
        props.style = [child.props.style, viewStyle, { width: own.iconSize, height: own.iconSize, flexShrink: 0 }];
      }
      if (text.color !== undefined) props.color = text.color;
    }
    if (kind === 'switch' && text.color !== undefined) props.thumbColor = text.color;
  }
  if (kind.startsWith('dom')) props.style = { ...child.props.style, ...elementStyle(text), ...style };
  if (kind === 'scope' && Object.keys(own).length) props.style = [child.props.style, style];
  return <ElementPathContext.Provider value={id}><InheritedTextContext.Provider value={text}>{React.cloneElement(child, props)}</InheritedTextContext.Provider></ElementPathContext.Provider>;
}
